import classNames from 'classnames';
import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import useLegacyIsLoggedIn from '../../account/useIsLoggedIn';
import OpacityMotionTransition from '../../components/OpacityMotionTransition';
import ScrollArea from '../../components/ScrollArea';
import { gql } from '../../graphql/generated/gql';
import { PostDetailPreviewBanner } from '../../post/PostDetailPreview';
import PostPreviewQuillComponent from '../../post/PostPreviewQuillComponent';
import {
  NotLoggedInPostViewError,
  RequestAccessPostViewError,
} from '../../post/PostViewError';
import { getHeadersForTOC } from '../../post/actionButtons/ConnectedTableOfContents';
import TableOfContents from '../../post/actionButtons/TableOfContents';
import { PostDetailPreviewStateBar } from '../../post/stateBar/PostStateBar';
import { parseShareSecretFromParams } from '../../post/useShareSecret';
import useIsPublicView from '../../utils/useIsPublicView';
import { AssetTokenProvider } from '../AssetTokenContext';
import { useQuill, withQuillContextProvider } from '../QuillContext';
import useAnchorScrollingHandler from '../useAnchorScrollingHandler';
import { parseMentionId } from '../utils/matchMention';
import { EmbedComponentScaleContainer } from './EmbedComponent';
import * as styles from './EmbedPostContent.module.scss';
import PostEmbedFooter from './PostEmbedFooter';

const TOC_TRANSITION = { duration: 0.15, type: 'tween' } as const;

const CONTENT_MAX_WIDTH = parseInt(styles.contentMaxWidth, 10);

gql(`
  fragment EmbedPostContent_post on PostFields {
    id
    content {
      id
      delta
    }
    ... on PublicPost {
      banner {
        original
      }
    }
    ... on Post {
      banner {
        original
      }
    }
    ...PostDetailPreviewStateBar_post
    ...PostEmbedFooter_post
    ...TableOfContents_post
  }
`);

const EMBED_POST_CONTENT_QUERY = gql(/** @complexity 15 */ `
  query embedPostContent($id: ID!, $shareSecret: String!) {
    post(id: $id, shareSecret: $shareSecret) {
      id
      ...EmbedPostContent_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

const EMBED_PUBLIC_POST_CONTENT_QUERY = gql(/** @complexity 15 */ `
  query embedPublicPostContent($id: ID!, $shareSecret: String!) {
    publicPost(id: $id, shareSecret: $shareSecret) {
      id
      ...EmbedPostContent_post
    }
    entityAssetToken(entityId: $id, entityType: POST) {
      ...AssetTokenProvider_entityAssetToken
    }
  }
`);

interface ContainerProps {
  children: React.ReactNode;
  hasFooter?: boolean;
}

const Container = ({ children, hasFooter }: ContainerProps) => (
  <div
    className={classNames(styles.container, {
      [styles.hasFooter]: hasFooter,
    })}
  >
    {children}
  </div>
);

interface EmbedPostContentProps {
  id: string;
  readOnly: boolean;
  initialShowingTOC?: boolean;
  params?: string;
}

const EmbedPostContent = ({
  id,
  readOnly,
  initialShowingTOC,
  params,
}: EmbedPostContentProps) => {
  const isLoggedIn = useLegacyIsLoggedIn();
  const isPublicView = useIsPublicView();

  const { id: postId, anchor } = parseMentionId(id);
  const shareSecret = parseShareSecretFromParams(params) ?? postId;

  const { data, loading } = useQuery(EMBED_POST_CONTENT_QUERY, {
    variables: { id: postId, shareSecret },
    skip: isPublicView,
  });

  const { data: publicData, loading: isPublicPostLoading } = useQuery(
    EMBED_PUBLIC_POST_CONTENT_QUERY,
    {
      variables: { id: postId, shareSecret },
      skip: !isPublicView,
    },
  );

  const { quill } = useQuill();
  const anchorScrollingHandlerProps = useAnchorScrollingHandler(quill, postId);

  const post = isPublicView ? publicData?.publicPost : data?.post;
  const entityAssetToken = isPublicView
    ? publicData?.entityAssetToken
    : data?.entityAssetToken;
  const isLoading = loading || isPublicPostLoading;

  const [headers, setHeaders] = useState<ReturnType<typeof getHeadersForTOC>>(
    [],
  );
  useEffect(() => {
    if (quill) {
      setHeaders(getHeadersForTOC(quill));
    }
  }, [quill, post?.content.delta]);

  const [isShowingTOC, setIsShowingTOC] = useState(initialShowingTOC ?? false);

  if (!isLoading && !post) {
    return (
      <Container>
        {isLoggedIn ? (
          <RequestAccessPostViewError postId={postId} />
        ) : (
          <NotLoggedInPostViewError postId={postId} />
        )}
      </Container>
    );
  }

  return (
    <>
      <Container hasFooter>
        <div className={styles.inner} {...anchorScrollingHandlerProps}>
          {post?.__typename === 'Post' && (
            <PostDetailPreviewStateBar
              post={post}
              contentMaxWidth={CONTENT_MAX_WIDTH}
              className={styles.stateBar}
            />
          )}
          {post?.banner?.original && (
            <PostDetailPreviewBanner banner={post.banner.original} />
          )}
          {!!post?.content && entityAssetToken && (
            <EmbedComponentScaleContainer>
              <AssetTokenProvider entityAssetToken={entityAssetToken}>
                <PostPreviewQuillComponent
                  autoHeight
                  content={post.content.delta}
                  anchor={anchor}
                  namespace="embedPostContent"
                />
              </AssetTokenProvider>
            </EmbedComponentScaleContainer>
          )}
        </div>
        <AnimatePresence>
          {isShowingTOC && (
            <>
              <OpacityMotionTransition
                className={styles.overlay}
                transition={TOC_TRANSITION}
                onClick={() => setIsShowingTOC(false)}
              />
              <motion.div
                className={styles.tocWrapper}
                initial={{ x: '100%' }}
                exit={{ x: '100%' }}
                animate={{ x: 0 }}
                transition={TOC_TRANSITION}
                {...anchorScrollingHandlerProps}
              >
                <ScrollArea className={styles.tocContainer}>
                  <h4 className={styles.tocTitle}>Table of Contents</h4>
                  <TableOfContents post={post} headers={headers} small />
                </ScrollArea>
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </Container>
      {post && (
        <PostEmbedFooter
          post={post}
          readOnly={readOnly}
          hasHeaders={headers.length > 0}
          isShowingTOC={isShowingTOC}
          onIsShowingTOCChange={setIsShowingTOC}
        />
      )}
    </>
  );
};

export default withQuillContextProvider(EmbedPostContent);
